<template>

  <div class="min-h-screen bg-[#f8fafc] font-sans">



    <!-- Top Navigation Bar -->

    <nav class="bg-white border-b border-gray-200 sticky top-0 z-50">

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">

        <div class="flex items-center gap-3 cursor-pointer" @click="$router.push('/')">

          <img src="@/assets/images/aboutUs/gbicon.svg" alt="GetBoarded" class="w-8 h-8 object-contain" />

          <span class="font-bold text-xl text-gray-900 tracking-tight">GetBoarded</span>

        </div>

        <vs-button type="border" color="#eab308" class="font-bold rounded-full px-6 shadow-sm" @click="$router.push('/')">

          Create Profile

        </vs-button>

      </div>

    </nav>



    <main class="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">



      <!-- LOADING & ERROR STATES -->

      <div v-if="loading" class="flex flex-col justify-center items-center h-64 gap-4">

        <feather-icon icon="LoaderIcon" class="w-10 h-10 text-primary animate-spin" />

        <span class="text-gray-500 font-semibold tracking-wide">Loading profile...</span>

      </div>



      <div v-else-if="error" class="flex justify-center items-center h-64">

        <div class="text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-200 max-w-md w-full">

          <feather-icon icon="LockIcon" class="w-12 h-12 text-gray-400 mx-auto mb-4" />

          <h2 class="text-xl font-bold text-gray-800 mb-2">Profile Unavailable</h2>

          <p class="text-gray-500 mb-6">{{ error }}</p>

        </div>

      </div>



      <!-- MAIN PROFILE VIEW -->

      <div v-else-if="orgData" class="space-y-6 lg:space-y-7">



        <!-- 1. HERO IDENTITY HEADER -->

        <div class="relative overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">



          <!-- Soft decorative background -->

          <div class="absolute rounded-full pointer-events-none" style="width: 180px; height: 180px; top: -80px; right: -50px; background: rgba(240, 191, 70, 0.08);"></div>

          <div class="absolute rounded-full pointer-events-none" style="width: 140px; height: 140px; bottom: -80px; left: -40px; background: rgba(240, 191, 70, 0.06);"></div>



          <!-- MAIN CONTENT -->

          <div class="relative z-10 flex flex-col lg:flex-row items-center" style="padding: 32px;">



            <!-- LOGO -->

            <div class="flex-shrink-0 mr-0 lg:mr-12 mb-5 lg:mb-0">

              <div

                style="width: 108px; height: 108px; transition: all 0.25s ease;"

                @mouseenter="$event.currentTarget.style.transform='translateY(-3px) scale(1.03)'"

                @mouseleave="$event.currentTarget.style.transform='translateY(0) scale(1)'"

              >

                <vs-avatar

                  v-if="orgData.logo"

                  class="m-0 !w-full !h-full overflow-hidden"

                  size="108px"

                  :src="constants.s3Bucket + '/organization/logo/' + orgData.logo"

                />

                <div

                  v-else

                  class="w-full h-full flex items-center justify-center rounded-xl font-bold text-yellow-600"

                  style="font-size: 38px; background: linear-gradient(135deg, #fff8df, #fff1bd);"

                >

                  {{ (orgData.name || orgData.companyName).charAt(0).toUpperCase() }}

                </div>

              </div>

            </div>



            <!-- COMPANY INFO -->

            <div class="flex-1 min-w-0 w-full lg:w-auto text-center lg:text-left">

              <h1 class="font-bold text-gray-900" style="font-size: 30px; line-height: 1.2; margin: 0 0 14px 0; letter-spacing: -0.5px;">

                {{ orgData.name || orgData.companyName }}

              </h1>



              <!-- META -->

              <div class="flex flex-wrap items-center justify-center lg:justify-start" style="gap: 8px; margin-bottom: 18px;">

                <span v-if="orgData.organizationType" class="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700" style="padding: 7px 11px; font-size: 13px; transition: all 0.2s ease;">

                  <feather-icon icon="BriefcaseIcon" style="width: 15px; height: 15px;" />

                  {{ orgData.organizationType }}

                </span>



                <span v-if="orgData.location" class="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg text-blue-700" style="padding: 7px 11px; font-size: 13px;">

                  <feather-icon icon="MapPinIcon" style="width: 15px; height: 15px;" />

                  {{ orgData.location }}

                </span>



                <a v-if="orgData.website" :href="formatUrl(orgData.website)" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg text-blue-600" style="padding: 7px 11px; font-size: 13px; font-weight: 600; text-decoration: none; transition: all 0.2s ease;" @mouseenter="$event.currentTarget.style.background='#dbeafe'" @mouseleave="$event.currentTarget.style.background='#eff6ff'">

                  <feather-icon icon="GlobeIcon" style="width: 15px; height: 15px;" />

                  <span>Company Website</span>

                  <feather-icon icon="ExternalLinkIcon" style="width: 12px; height: 12px;" />

                </a>

              </div>



              <!-- STATUS -->

              <div class="flex flex-wrap items-center justify-center lg:justify-start" style="gap: 8px;">

                <span v-if="openRolesCount > 0 || (orgData.openRoles && orgData.openRoles.length > 0)" class="inline-flex items-center gap-2 bg-green-50 border border-green-100 rounded-full text-green-700 font-semibold" style="padding: 7px 12px; font-size: 12px;">

                  <span class="rounded-full bg-green-500" style="width: 7px; height: 7px;"></span>

                  Open To Hiring

                </span>

                <span class="inline-flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-full text-purple-700 font-semibold" style="padding: 7px 12px; font-size: 12px;">

                  <span class="rounded-full bg-purple-500" style="width: 7px; height: 7px;"></span>

                  Open To Partnerships

                </span>

              </div>

            </div>



            <!-- ACTION BUTTONS -->

            <div class="flex-shrink-0 w-full lg:w-auto mt-6 lg:mt-0 lg:ml-8 border-t border-gray-100 pt-5 lg:border-t-0 lg:border-l lg:pl-6 lg:pt-0">

              <div class="flex flex-col sm:flex-row lg:flex-col" style="gap: 10px; min-width: 230px;">

                <a v-if="orgData.website" :href="formatUrl(orgData.website)" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center rounded-xl font-semibold" style="height: 46px; padding: 0 18px; background: #F0BF46; color: #111827; text-decoration: none; font-size: 14px; transition: all 0.25s ease; box-shadow: 0 3px 10px rgba(240, 191, 70, 0.15);" @mouseenter="$event.currentTarget.style.transform='translateY(-2px)'; $event.currentTarget.style.background='#eab535'; $event.currentTarget.style.boxShadow='0 7px 18px rgba(240,191,70,0.28)'" @mouseleave="$event.currentTarget.style.transform='translateY(0)'; $event.currentTarget.style.background='#F0BF46'; $event.currentTarget.style.boxShadow='0 3px 10px rgba(240,191,70,0.15)'">

                  <feather-icon icon="ExternalLinkIcon" class="mr-2" style="width: 17px; height: 17px;" />

                  <span>Visit Website</span>

                  <feather-icon icon="ArrowRightIcon" class="ml-auto" style="width: 15px; height: 15px;" />

                </a>



                <div class="flex flex-col" style="gap: 8px;">

                  <a :href="orgData.workEmail ? `mailto:${orgData.workEmail}` : '#'" class="inline-flex items-center justify-center rounded-xl font-semibold bg-white border border-gray-300 text-gray-800" style="height: 46px; padding: 0 18px; font-size: 14px; text-decoration: none; transition: all 0.25s ease;" @mouseenter="$event.currentTarget.style.transform='translateY(-2px)'; $event.currentTarget.style.borderColor='#9ca3af'; $event.currentTarget.style.boxShadow='0 5px 15px rgba(0,0,0,0.07)'" @mouseleave="$event.currentTarget.style.transform='translateY(0)'; $event.currentTarget.style.borderColor='#d1d5db'; $event.currentTarget.style.boxShadow='none'">

                    <feather-icon icon="MailIcon" class="mr-2 text-gray-500" style="width: 17px; height: 17px;" />

                    <span>Contact Us</span>

                    <feather-icon icon="ArrowRightIcon" class="ml-auto text-gray-400" style="width: 15px; height: 15px;" />

                  </a>

                  <nav class="org-section-nav flex items-center justify-center" aria-label="Organization profile sections">
                    <button type="button" class="org-section-icon" :class="{ 'is-active': activeTab === 'Overview' }" data-tooltip="Overview" aria-label="Overview" :aria-current="activeTab === 'Overview' ? 'page' : null" @click="activeTab = 'Overview'">
                      <feather-icon icon="HomeIcon" style="width: 19px; height: 19px;" />
                    </button>
                    <span class="org-section-separator" aria-hidden="true">|</span>
                    <button type="button" class="org-section-icon" :class="{ 'is-active': activeTab === 'People & Culture' }" data-tooltip="People & Culture" aria-label="People & Culture" :aria-current="activeTab === 'People & Culture' ? 'page' : null" @click="activeTab = 'People & Culture'">
                      <feather-icon icon="UsersIcon" style="width: 19px; height: 19px;" />
                    </button>
                    <span class="org-section-separator" aria-hidden="true">|</span>
                    <button type="button" class="org-section-icon" :class="{ 'is-active': activeTab === 'Opportunities' }" data-tooltip="Opportunities" aria-label="Opportunities" :aria-current="activeTab === 'Opportunities' ? 'page' : null" @click="activeTab = 'Opportunities'">
                      <feather-icon icon="BriefcaseIcon" style="width: 19px; height: 19px;" />
                    </button>
                    <span class="org-section-separator" aria-hidden="true">|</span>
                    <button type="button" class="org-section-icon" :class="{ 'is-active': activeTab === 'Resources' }" data-tooltip="Resources" aria-label="Resources" :aria-current="activeTab === 'Resources' ? 'page' : null" @click="activeTab = 'Resources'">
                      <feather-icon icon="FileTextIcon" style="width: 19px; height: 19px;" />
                    </button>
                  </nav>

                </div>

              </div>

            </div>



          </div>

        </div>



        <!-- 3. OVERVIEW: keep related information together and collapse missing content cleanly. -->
        <section v-show="activeTab === 'Overview'" class="space-y-7 animate-fade-in">
          <div
            :class="orgData.about
              ? 'grid grid-cols-1 lg:grid-cols-12 gap-6 items-start'
              : 'grid grid-cols-1 gap-6'"
          >
            <!-- ABOUT: only reserve the left column when there is actual content. -->
            <section v-if="orgData.about" class="lg:col-span-8 min-w-0">
              <div class="h-full bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-7">
                <div class="flex items-center gap-3 mb-4">
                  <div class="flex items-center justify-center w-9 h-9 rounded-xl bg-amber-50 text-amber-700">
                    <feather-icon icon="InfoIcon" class="w-5 h-5" />
                  </div>
                  <h2 class="text-lg font-bold text-gray-900">About</h2>
                </div>
                <div class="text-sm md:text-base text-gray-600 leading-7 whitespace-pre-line">
                  {{ orgData.about }}
                </div>
              </div>
            </section>

            <!-- AT A GLANCE -->
            <aside :class="orgData.about ? 'lg:col-span-4 min-w-0 space-y-5' : 'w-full min-w-0 space-y-5'">
              <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
                <div class="mb-5">
                  <h2 class="text-lg font-bold text-gray-900">At a glance</h2>
                  <p class="text-sm text-gray-500 mt-1">A quick view of the organization</p>
                </div>

                <div :class="orgData.about ? 'space-y-5' : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4'">
                  <div
                    v-if="orgData.organizationType"
                    :class="orgData.about ? 'flex items-start gap-3 min-w-0' : 'flex items-start gap-3 min-w-0 rounded-xl border border-gray-100 bg-gray-50 p-4'"
                  >
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                      <feather-icon icon="BriefcaseIcon" class="w-4 h-4" />
                    </div>
                    <div class="min-w-0">
                      <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Organization type</span>
                      <span class="text-sm font-semibold text-gray-800 block mt-1">{{ orgData.organizationType }}</span>
                    </div>
                  </div>

                  <div
                    v-if="orgData.employeeCount !== undefined"
                    :class="orgData.about ? 'flex items-start gap-3 min-w-0' : 'flex items-start gap-3 min-w-0 rounded-xl border border-gray-100 bg-gray-50 p-4'"
                  >
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-sky-50 text-sky-600 shrink-0">
                      <feather-icon icon="UsersIcon" class="w-4 h-4" />
                    </div>
                    <div class="min-w-0">
                      <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Talent community</span>
                      <span class="text-sm font-semibold text-gray-800 block mt-1">{{ orgData.employeeCount }} members</span>
                      <span v-if="evaluatedMembersCount > 0" class="inline-block mt-2 rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                        <strong>{{ evaluatedMembersCount }}</strong> in evaluated roles
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="orgData.totalRoles !== undefined"
                    :class="orgData.about ? 'flex items-start gap-3 min-w-0' : 'flex items-start gap-3 min-w-0 rounded-xl border border-gray-100 bg-gray-50 p-4'"
                  >
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-purple-50 text-purple-600 shrink-0">
                      <feather-icon icon="LayersIcon" class="w-4 h-4" />
                    </div>
                    <div class="min-w-0">
                      <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Key roles</span>
                      <span class="text-sm font-semibold text-gray-800 block mt-1">{{ orgData.totalRoles }} total roles</span>
                      <span v-if="orgData.openRoles && orgData.openRoles.length > 0" class="inline-block mt-2 rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700">
                        <strong>{{ orgData.openRoles.length }}</strong> open for hiring
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="orgData.location"
                    :class="orgData.about ? 'flex items-start gap-3 min-w-0' : 'flex items-start gap-3 min-w-0 rounded-xl border border-gray-100 bg-gray-50 p-4'"
                  >
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-50 text-amber-700 shrink-0">
                      <feather-icon icon="MapPinIcon" class="w-4 h-4" />
                    </div>
                    <div class="min-w-0">
                      <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Headquarters</span>
                      <span class="text-sm font-semibold text-gray-800 block mt-1 break-words">{{ orgData.location }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Keep the existing quick document preview feature. -->
              <div v-if="sidebarDocuments.length > 0" class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <h3 class="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <feather-icon icon="FileTextIcon" class="w-4 h-4 text-orange-500" />
                  Documents
                </h3>
                <div class="space-y-2">
                  <div
                    v-for="(doc, idx) in sidebarDocuments"
                    :key="'side-doc-' + idx"
                    class="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-300 transition-colors group cursor-pointer"
                    @click="previewDocument(doc)"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5 shrink-0', getFileIconColor(doc.file_extension)]" />
                      <div class="min-w-0">
                        <p class="text-xs font-semibold text-left text-gray-800 truncate" :title="doc.original_filename">{{ doc.original_filename }}</p>
                        <p class="text-[10px] text-gray-500 text-left uppercase mt-1">{{ doc.file_extension }} · {{ doc.file_size }}</p>
                      </div>
                    </div>
                    <feather-icon icon="EyeIcon" class="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors shrink-0" />
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <!-- OPEN OPPORTUNITIES PREVIEW -->
          <section v-if="orgData.openRoles && orgData.openRoles.length > 0" class="space-y-4">
            <div class="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 class="text-xl font-bold text-gray-900">Open opportunities</h2>
                <p class="text-sm text-gray-500 mt-1">Explore ways to collaborate with this organization.</p>
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-2 border-0 bg-transparent p-2 text-sm font-semibold text-gray-700 rounded-lg cursor-pointer hover:bg-amber-50 hover:text-amber-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                @click="activeTab = 'Opportunities'"
              >
                View all <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <article
                v-for="(role, idx) in paginatedRoles"
                :key="'overview-role-' + idx"
                class="min-w-0 rounded-2xl border border-gray-200 bg-white p-5 flex flex-col transition-all duration-200 hover:border-amber-200 hover:shadow-md"
                style="min-height: 160px;"
              >
                <span class="inline-flex items-center w-max rounded-md bg-amber-50 text-amber-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider mb-3">Open role</span>
                <h3 class="text-base font-semibold leading-6 text-gray-900 mb-2 break-words">{{ role.title }}</h3>
                <p v-if="role.skills && role.skills.length" class="text-sm text-gray-500 mb-4 leading-5 line-clamp-2" style="text-transform: capitalize;">{{ role.skills.join(', ') }}</p>
                <p v-else class="text-sm text-gray-400 italic mb-4">To be discussed during the interview process.</p>
                <div class="flex items-center gap-2 text-sm text-gray-500 mt-auto">
                  <feather-icon icon="MapPinIcon" class="w-4 h-4 text-gray-400" />
                  <span>{{ orgData.location || 'Remote' }}</span>
                </div>
              </article>
            </div>
          </section>

          <!-- LEADERSHIP PREVIEW -->
          <section v-if="orgData.leadership && orgData.leadership.length > 0" class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
            <div class="flex flex-wrap items-end justify-between gap-3 mb-5">
              <div>
                <h2 class="text-xl font-bold text-gray-900">Leadership &amp; team</h2>
                <p class="text-sm text-gray-500 mt-1">Meet the people behind the organization.</p>
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-2 border-0 bg-transparent p-2 text-sm font-semibold text-gray-700 rounded-lg cursor-pointer hover:bg-amber-50 hover:text-amber-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                @click="activeTab = 'People & Culture'"
              >
                Meet the team <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-3">
              <div v-for="(leader, idx) in orgData.leadership" :key="'overview-leader-' + idx" class="flex min-w-0 items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 hover:bg-white hover:border-gray-200 transition-colors">
                <vs-avatar size="44px" :src="getAvatarUrl(leader)" class="m-0 flex-shrink-0 border border-gray-200" />
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900 leading-5 break-words">{{ leader.firstName }} {{ leader.lastName }}</p>
                  <p class="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mt-1 leading-4 break-words">{{ leader.role }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- RESOURCES PREVIEW -->
          <section v-if="attachedDocumentsList.length > 0 || (mediaResourcesList.length > 0 && attachedDocumentsList.length === 0)" class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
            <div class="flex flex-wrap items-end justify-between gap-3 mb-5">
              <div>
                <h2 class="text-xl font-bold text-gray-900">Resources</h2>
                <p class="text-sm text-gray-500 mt-1">Documents and materials shared by the organization.</p>
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-2 border-0 bg-transparent p-2 text-sm font-semibold text-gray-700 rounded-lg cursor-pointer hover:bg-amber-50 hover:text-amber-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                @click="activeTab = 'Resources'"
              >
                View resources <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
              </button>
            </div>

            <div v-if="attachedDocumentsList.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                v-for="(doc, idx) in attachedDocumentsList.slice(0, 2)"
                :key="'overview-doc-' + idx"
                class="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div class="flex min-w-0 items-center gap-3">
                  <div :class="['flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', getFileBgColor(doc.file_extension)]">
                    <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5', getFileIconColor(doc.file_extension)]" />
                  </div>
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-left text-gray-900" :title="doc.original_filename">{{ doc.original_filename }}</p>
                    <p class="mt-1 text-[10px] font-medium uppercase text-left tracking-wide text-gray-400">{{ doc.file_extension }} · {{ doc.file_size }}</p>
                  </div>
                </div>
                <div class="ml-2 flex shrink-0 items-center gap-1">
                  <button type="button" @click.stop="previewDocument(doc)" class="border-0 bg-transparent p-2 text-gray-400 hover:text-blue-500 cursor-pointer" aria-label="Preview document">
                    <feather-icon icon="EyeIcon" class="w-5 h-5" />
                  </button>
                  <button type="button" @click.stop="downloadFile(doc.original_file_url, doc.original_filename)" class="border-0 bg-transparent p-2 text-gray-400 hover:text-green-600 cursor-pointer" aria-label="Download document">
                    <feather-icon icon="DownloadIcon" class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div v-else-if="mediaResourcesList.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                v-for="(doc, idx) in mediaResourcesList.slice(0, 2)"
                :key="'overview-media-' + idx"
                class="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div class="flex min-w-0 items-center gap-3">
                  <div :class="['flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', getFileBgColor(doc.file_extension)]">
                    <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5', getFileIconColor(doc.file_extension)]" />
                  </div>
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-left text-gray-900" :title="doc.original_filename">{{ doc.original_filename }}</p>
                    <p class="mt-1 text-[10px] font-medium uppercase text-left tracking-wide text-gray-400">{{ doc.file_extension }} · {{ doc.file_size }}</p>
                  </div>
                </div>
                <div class="ml-2 flex shrink-0 items-center gap-1">
                  <button v-if="isImageFile(doc.file_extension)" type="button" @click.stop="previewDocument(doc)" class="border-0 bg-transparent p-2 text-gray-400 hover:text-blue-500 cursor-pointer" aria-label="Preview resource">
                    <feather-icon icon="EyeIcon" class="w-5 h-5" />
                  </button>
                  <button type="button" @click.stop="downloadFile(doc.original_file_url, doc.original_filename)" class="border-0 bg-transparent p-2 text-gray-400 hover:text-green-600 cursor-pointer" aria-label="Download resource">
                    <feather-icon icon="DownloadIcon" class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </section>
        </section>

        <!-- 4. PEOPLE & CULTURE: this tab has its own full-width layout. -->
        <section v-show="activeTab === 'People & Culture'" class="animate-fade-in">
          <div v-if="orgData.leadership && orgData.leadership.length > 0" class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-7">
            <div class="mb-6">
              <p class="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">The people</p>
              <h2 class="text-2xl font-bold text-gray-900">Leadership &amp; team</h2>
              <p class="text-sm text-gray-500 mt-2">Meet the people guiding and supporting this organization.</p>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              <div v-for="(leader, idx) in orgData.leadership" :key="'team-leader-' + idx" class="flex min-w-0 items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all duration-200 hover:border-amber-200 hover:shadow-sm">
                <vs-avatar size="52px" :src="getAvatarUrl(leader)" class="m-0 flex-shrink-0 border border-gray-200" />
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900 leading-5 break-words">{{ leader.firstName }} {{ leader.lastName }}</p>
                  <p class="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mt-1 leading-4 break-words">{{ leader.role }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-16 px-6 bg-white rounded-2xl border border-gray-200">
            <feather-icon icon="UsersIcon" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h2 class="text-lg font-semibold text-gray-900">Team information unavailable</h2>
            <p class="text-sm text-gray-500 mt-2">This organization has not shared its leadership details yet.</p>
          </div>
        </section>

        <!-- 5. OPPORTUNITIES: full-width grid with a clear empty state and pagination. -->
        <section v-show="activeTab === 'Opportunities'" class="animate-fade-in">
          <div class="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <p class="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">Work together</p>
              <h2 class="text-2xl font-bold text-gray-900">Open opportunities</h2>
              <p class="text-sm text-gray-500 mt-2">Explore roles and ways to collaborate with this organization.</p>
            </div>
            <span v-if="orgData.openRoles && orgData.openRoles.length" class="inline-flex items-center rounded-full bg-white border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600">
              {{ orgData.openRoles.length }} {{ orgData.openRoles.length === 1 ? 'opportunity' : 'opportunities' }}
            </span>
          </div>

          <div v-if="orgData.openRoles && orgData.openRoles.length > 0">
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <article v-for="(role, idx) in paginatedRoles" :key="'all-role-' + idx" class="min-w-0 rounded-2xl border border-gray-200 bg-white p-5 flex flex-col transition-all duration-200 hover:border-amber-200 hover:shadow-md" style="min-height: 170px;">
                <span class="inline-flex items-center w-max rounded-md bg-amber-50 text-amber-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider mb-3">Open role</span>
                <h3 class="text-base font-semibold leading-6 text-gray-900 mb-2 break-words">{{ role.title }}</h3>
                <p v-if="role.skills && role.skills.length" class="text-sm text-gray-500 mb-4 leading-5 line-clamp-2" style="text-transform: capitalize;">{{ role.skills.join(', ') }}</p>
                <p v-else class="text-sm text-gray-400 italic mb-4">To be discussed during the interview process.</p>
                <div class="flex items-center gap-2 text-sm text-gray-500 mt-auto">
                  <feather-icon icon="MapPinIcon" class="w-4 h-4 text-gray-400" />
                  <span>{{ orgData.location || 'Remote' }}</span>
                </div>
              </article>
            </div>
            <div v-if="totalRolePages > 1" class="mt-8 flex justify-center border-t border-gray-200 pt-6">
              <vs-pagination :total="totalRolePages" v-model="rolesCurrentPage" color="#eab308" />
            </div>
          </div>
          <div v-else class="text-center py-16 px-6 bg-white rounded-2xl border border-gray-200">
            <feather-icon icon="BriefcaseIcon" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 class="text-lg font-semibold text-gray-900">No open opportunities right now</h3>
            <p class="text-sm text-gray-500 mt-2">Please check back later for new roles and collaborations.</p>
          </div>
        </section>

        <!-- 6. RESOURCES: full-width and only visible in the Resources tab. -->
        <section v-show="activeTab === 'Resources'" class="space-y-6 animate-fade-in">
          <div class="mb-2">
            <p class="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">Explore and learn</p>
            <h2 class="text-2xl font-bold text-gray-900">Resources</h2>
            <p class="text-sm text-gray-500 mt-2">Documents and materials shared by the organization.</p>
          </div>

          <section v-if="attachedDocumentsList.length > 0" class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
            <h3 class="text-lg font-bold text-gray-900 mb-5">Documents</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              <div v-for="(doc, idx) in attachedDocumentsList" :key="'res-doc-' + idx" class="resource-item group flex min-w-0 items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 transition-all duration-200">
                <div class="flex min-w-0 items-center gap-3">
                  <div :class="['flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', getFileBgColor(doc.file_extension)]">
                    <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5', getFileIconColor(doc.file_extension)]" />
                  </div>
                  <div class="min-w-0 overflow-hidden">
                    <p class="truncate text-sm font-semibold text-left text-gray-900" :title="doc.original_filename">{{ doc.original_filename }}</p>
                    <p class="mt-1 text-[10px] font-medium uppercase text-left tracking-wide text-gray-400">{{ doc.file_extension }} · {{ doc.file_size }}</p>
                  </div>
                </div>
                <div class="ml-2 flex shrink-0 items-center gap-1">
                  <button type="button" @click.stop="previewDocument(doc)" class="resource-action resource-action-preview" aria-label="Preview document" title="Preview document"><feather-icon icon="EyeIcon" class="w-5 h-5" /></button>
                  <button type="button" @click.stop="downloadFile(doc.original_file_url, doc.original_filename)" class="resource-action resource-action-download" aria-label="Download document" title="Download document"><feather-icon icon="DownloadIcon" class="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          </section>

          <section v-if="mediaResourcesList.length > 0" class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
            <h3 class="text-lg font-bold text-gray-900 mb-5">Media</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              <div v-for="(doc, idx) in mediaResourcesList" :key="'res-media-' + idx" class="resource-item group flex min-w-0 items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 transition-all duration-200">
                <div class="flex min-w-0 items-center gap-3">
                  <div :class="['flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', getFileBgColor(doc.file_extension)]">
                    <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5', getFileIconColor(doc.file_extension)]" />
                  </div>
                  <div class="min-w-0 overflow-hidden">
                    <p class="truncate text-sm font-semibold text-left text-gray-900" :title="doc.original_filename">{{ doc.original_filename }}</p>
                    <p class="mt-1 text-[10px] font-medium uppercase text-left tracking-wide text-gray-400">{{ doc.file_extension }} · {{ doc.file_size }}</p>
                  </div>
                </div>
                <div class="ml-2 flex shrink-0 items-center gap-1">
                  <button v-if="isImageFile(doc.file_extension)" type="button" @click.stop="previewDocument(doc)" class="resource-action resource-action-preview" aria-label="Preview resource" title="Preview resource"><feather-icon icon="EyeIcon" class="w-5 h-5" /></button>
                  <button type="button" @click.stop="downloadFile(doc.original_file_url, doc.original_filename)" class="resource-action resource-action-download" aria-label="Download resource" title="Download resource"><feather-icon icon="DownloadIcon" class="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          </section>

          <div v-if="publicDocuments.length === 0" class="text-center py-16 px-6 bg-white rounded-2xl border border-gray-200">
            <feather-icon icon="FileTextIcon" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 class="text-lg font-semibold text-gray-900">No public resources yet</h3>
            <p class="text-sm text-gray-500 mt-2">This organization has not shared any documents or resources.</p>
          </div>
        </section>

      </div>

    </main>



    <!-- MEGA PDF/IMAGE PREVIEW MODAL -->

    <vs-popup :title="previewIsImage ? 'Image Preview' : 'Document Preview'" :active.sync="previewActive" class="mega-preview-modal">

      <div v-if="previewActive" class="w-full flex flex-col items-center justify-center bg-gray-100" style="height: 600px; max-height: 80vh;">



        <!-- Render Image -->

        <img 

          v-if="previewIsImage" 

          :src="currentPreviewUrl" 

          class="max-w-full max-h-full object-contain p-4"

          alt="Preview"

        />



        <!-- Render PDF in Iframe -->

        <iframe 

          v-else

          :src="currentPreviewUrl + '#toolbar=0&navpanes=0&scrollbar=0'" 

          class="flex-grow w-full border-0 bg-gray-100 rounded-b-lg"

          frameborder="0"

        ></iframe>



      </div>

    </vs-popup>



  </div>

</template>



<script>

import constants from "@/constants.js";



export default {

  name: "PublicOrganizationProfile",

  data() {

    return {

      constants,

      loading: true,

      error: null,

      orgData: null,



      activeTab: 'Overview', 



      rolesCurrentPage: 1,

      rolesPerPage: 6,



      // KVDR dynamic metrics

      evaluatedMembersCount: 0,

      openRolesCount: 0,



      // Document specific data

      publicDocuments: [],

      documentTypes: ['pdf', 'ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'],

      previewActive: false,

      previewIsImage: false, // <-- ADD THIS

      currentPreviewUrl: "",

    };

  },

  computed: {

    paginatedRoles() {

      if (!this.orgData || !this.orgData.openRoles) return [];

      const start = (this.rolesCurrentPage - 1) * this.rolesPerPage;

      const end = start + this.rolesPerPage;

      return this.orgData.openRoles.slice(start, end);

    },

    totalRolePages() {

      if (!this.orgData || !this.orgData.openRoles) return 0;

      return Math.ceil(this.orgData.openRoles.length / this.rolesPerPage);

    },

    apiUrl() {

      const url = process.env.VUE_APP_API_URL || "http://127.0.0.1:8000";

      return url.endsWith('/') ? url.slice(0, -1) : url;

    },

    // Filtering Documents natively for attached list

    attachedDocumentsList() {

      return this.publicDocuments.filter(doc => 

        this.documentTypes.includes((doc.file_extension || '').toLowerCase())

      );

    },

    // Filtering everything else into media resources (images, videos, etc.)

    mediaResourcesList() {

      return this.publicDocuments.filter(doc => 

        !this.documentTypes.includes((doc.file_extension || '').toLowerCase())

      );

    },

    // Sidebar strict limitation to 3 valid docs

    sidebarDocuments() {

      return this.attachedDocumentsList.slice(0, 3);

    }

  },

  created() {

    this.fetchPublicProfile();

  },

  methods: {

    isImageFile(ext) {

      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes((ext || '').toLowerCase());

    },

    async fetchPublicProfile() {

      this.loading = true;

      this.error = null;

      const slug = this.$route.params.slug; 



      try {

        const response = await fetch(`${this.apiUrl}/api/public/organization/${slug}`);

        const result = await response.json();



        if (response.ok && result.status === 'success') {

          this.orgData = result.data;



          // FIX: Add broader fallbacks to ensure we capture the ID properly from public data

          const actualOrgId = this.orgData._id || this.orgData.id || this.orgData.organizationId || this.orgData.orgId;



          await Promise.all([

            this.fetchPublicDocuments(actualOrgId),

            this.fetchKvdrStats(actualOrgId)

          ]);



        } else {

          this.error = result.detail || "Organization profile is private or does not exist.";

        }

      } catch (err) {

        this.error = err.message || "An error occurred while loading the profile.";

      } finally {

        this.loading = false;

      }

    },



    async fetchPublicDocuments(orgId) {

      if (!orgId) return;

      try {

        const docRes = await fetch(`${this.apiUrl}/api/v2/organization/${orgId}/documents?public_only=true`);

        const docResult = await docRes.json();

        if (docRes.ok && docResult.status === 'success') {

          this.publicDocuments = docResult.data || [];

        }

      } catch (e) {

        console.error("Failed to fetch public documents", e);

      }

    },



    async fetchKvdrStats(orgId) {

      if (!orgId) return;

      try {

        const kvdrRes = await fetch(`https://api.getboarded.tech/kvdr?orgId=${orgId}`);

        const kvdrData = await kvdrRes.json();



        let uniqueEvaluatedUsers = new Set();



        // 1. Get the main data array

        const dataArray = Array.isArray(kvdrData) ? kvdrData : (kvdrData.data || []);



        // 2. Loop through the main documents

        dataArray.forEach(kvdrDoc => {



          // 3. Loop through all the roles (CEO, CTO, Backend Dev, etc.)

          if (kvdrDoc.kvdrRoles && Array.isArray(kvdrDoc.kvdrRoles)) {

            kvdrDoc.kvdrRoles.forEach(role => {



              // 4. Loop through all evaluated profiles under that specific role

              if (role.kvdrRoleProfiles && Array.isArray(role.kvdrRoleProfiles)) {

                role.kvdrRoleProfiles.forEach(profile => {



                  // 5. Finally, grab the userId!

                  if (profile.userId) {

                    uniqueEvaluatedUsers.add(profile.userId.toString());

                  }



                });

              }

            });

          }

        });



        this.evaluatedMembersCount = uniqueEvaluatedUsers.size;



      } catch (e) {

        console.error("Failed to fetch KVDR stats", e);

      }

    },



    previewDocument(doc) {

      if (!doc) return;



      if (this.isImageFile(doc.file_extension)) {

        // It's an image, use original URL

        this.currentPreviewUrl = `${this.apiUrl}${doc.original_file_url}`;

        this.previewIsImage = true;

      } else {

        // It's a document, use PDF URL

        if (!doc.pdf_file_url) {

          this.$vs.notify({ title: 'Processing', text: 'Document is still being prepared. Try again in a few seconds.', color: 'warning' });

          return;

        }

        this.currentPreviewUrl = `${this.apiUrl}${doc.pdf_file_url}`;

        this.previewIsImage = false;

      }

      this.previewActive = true;

    },



    downloadFile(fileUrl, fileName) {

      if (!fileUrl) return;

      const link = document.createElement('a');

      link.href = `${this.apiUrl}${fileUrl}`;

      link.setAttribute('download', fileName);

      link.setAttribute('target', '_blank');

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

    },



    formatUrl(url) {

      if (!url) return '#';

      if (!url.match(/^https?:\/\//i)) {

        return 'http://' + url;

      }

      return url;

    },



    getAvatarUrl(leader) {

      if (leader.profilePictureUrl) {

        if (leader.profilePictureUrl.startsWith('http')) return leader.profilePictureUrl;

        return `${this.constants.s3Bucket}/editor/profile-pictures/${leader.profilePictureUrl}`;

      }

      const name = encodeURIComponent(`${leader.firstName} ${leader.lastName}`);

      return `https://ui-avatars.com/api/?name=${name}&background=f1f5f9&color=475569&bold=true`;

    },



    // --- UI Helpers for File Types ---

    getFileIcon(ext) {

      const e = (ext || '').toLowerCase();

      switch (e) {

        case 'pdf': return 'FileTextIcon';

        case 'ppt': 

        case 'pptx': return 'MonitorIcon';

        case 'doc': 

        case 'docx': return 'FileIcon';

        case 'xls': 

        case 'xlsx': 

        case 'csv': return 'GridIcon';

        case 'png':

        case 'jpg':

        case 'jpeg': return 'ImageIcon';

        case 'mp4':

        case 'avi':

        case 'mov': return 'VideoIcon';

        default: return 'FileIcon';

      }

    },

    getFileBgColor(ext) {

      const e = (ext || '').toLowerCase();

      switch (e) {

        case 'pdf': return 'bg-red-50';

        case 'ppt': 

        case 'pptx': return 'bg-orange-50';

        case 'doc': 

        case 'docx': return 'bg-blue-50';

        case 'xls': 

        case 'xlsx': 

        case 'csv': return 'bg-green-50';

        case 'png':

        case 'jpg':

        case 'jpeg': return 'bg-purple-50';

        case 'mp4':

        case 'avi':

        case 'mov': return 'bg-indigo-50';

        default: return 'bg-gray-100';

      }

    },

    getFileIconColor(ext) {

      const e = (ext || '').toLowerCase();

      switch (e) {

        case 'pdf': return 'text-red-600';

        case 'ppt': 

        case 'pptx': return 'text-orange-600';

        case 'doc': 

        case 'docx': return 'text-blue-600';

        case 'xls': 

        case 'xlsx': 

        case 'csv': return 'text-green-600';

        case 'png':

        case 'jpg':

        case 'jpeg': return 'text-purple-600';

        case 'mp4':

        case 'avi':

        case 'mov': return 'text-indigo-600';

        default: return 'text-gray-600';

      }

    }

  }

};

</script>



<style scoped>

/* Custom Scrollbar Hiding for tabs container on mobile */

.hide-scrollbar::-webkit-scrollbar {

  display: none;

}

.hide-scrollbar {

  -ms-overflow-style: none;

  scrollbar-width: none;

}



/* Smooth Fade In for Tab Content */

.animate-fade-in {

  animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);

}

@keyframes fadeIn {

  from { opacity: 0; transform: translateY(8px); }

  to { opacity: 1; transform: translateY(0); }

}



/* Resource cards: visible hover feedback without making the whole row clickable. */
.resource-item {
  border-color: #e5e7eb;
  background-color: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
}

.resource-item:hover {
  border-color: #f0bf46;
  background-color: #fffdf5;
  box-shadow: 0 8px 20px rgba(31, 41, 55, 0.09);
  transform: translateY(-2px);
}

.resource-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.18s ease, background-color 0.18s ease, transform 0.18s ease;
  outline: none;
}

.resource-action:hover {
  transform: translateY(-1px);
}

.resource-action-preview:hover,
.resource-action-preview:focus-visible {
  color: #2563eb;
  background-color: #eff6ff;
}

.resource-action-download:hover,
.resource-action-download:focus-visible {
  color: #15803d;
  background-color: #f0fdf4;
}

.resource-action:focus-visible {
  box-shadow: 0 0 0 2px rgba(240, 191, 70, 0.45);
}

/* =========================================================

   LARGE DOCUMENT PREVIEW

   ========================================================= */



/* Ensure the popup brings itself up with maximum space */

::v-deep .mega-preview-modal .vs-popup {

  width: 90vw !important;

  max-width: 800px !important; /* Changed from 1400px to 800px */

  height: auto !important; 

  margin: auto !important;

  display: flex !important;

  flex-direction: column !important;

  border-radius: 12px !important;

  overflow: hidden !important;

}



::v-deep .mega-preview-modal .vs-popup--content {

  width: 100% !important;

  height: 100% !important;

  flex-grow: 1 !important;

  padding: 0 !important;

  margin: 0 !important;

  overflow: hidden !important;

  display: flex !important;

  flex-direction: column !important;

}


/* Compact icon-only navigation beneath Contact Us. */
.org-section-nav {
  gap: 10px;
  min-height: 28px;
}

.org-section-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 3px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;
  transition: color 0.18s ease, transform 0.18s ease;
  outline: none;
}

.org-section-icon:hover,
.org-section-icon:focus-visible,
.org-section-icon.is-active {
  color: #d97706;
}

.org-section-icon:hover {
  transform: translateY(-1px);
}

.org-section-icon:focus-visible {
  box-shadow: 0 0 0 2px rgba(240, 191, 70, 0.45);
}

.org-section-icon::after {
  content: attr(data-tooltip);
  position: absolute;
  z-index: 100;
  left: 50%;
  bottom: calc(100% + 8px);
  padding: 6px 9px;
  border-radius: 5px;
  background: #1f2937;
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translate(-50%, 4px);
  transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s ease;
}

.org-section-icon:hover::after,
.org-section-icon:focus-visible::after {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, 0);
}

.org-section-separator {
  color: #d1d5db;
  font-size: 15px;
  line-height: 1;
  user-select: none;
}

</style>